import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API_URL, todayISO, formatUSD, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, FileDown, Database, Wrench, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DataTab() {
  const { activeOrgId } = useAuth();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [repair, setRepair] = useState(null); // { would_reclassify, applied, sample, target_fund }
  const [repairBusy, setRepairBusy] = useState(false);

  const download = async (path, filename) => {
    const token = localStorage.getItem("ledger_token") || "";
    try {
      const res = await fetch(`${API_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${filename} downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  const buildRangeQS = () => {
    const params = new URLSearchParams();
    if (start) params.set("start_date", start);
    if (end) params.set("end_date", end);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  const fnameSuffix = start && end ? `-${start}_to_${end}` : "";

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-card rounded-3xl p-7">
        <h3 className="font-serif text-2xl flex items-center gap-2"><Database className="w-5 h-5 text-primary" /> Export your data</h3>
        <p className="text-sm text-muted-foreground mt-1.5">Download everything as an Excel workbook (4 sheets) or specific slices as CSV. Optionally restrict transactions to a date range.</p>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="space-y-1.5">
            <Label>Start date (optional)</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl" data-testid="data-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label>End date (optional)</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl" data-testid="data-end-date" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="border-0 shadow-sm bg-card rounded-3xl p-6 transition-all hover:shadow-md">
          <FileSpreadsheet className="w-7 h-7 text-primary" />
          <h4 className="font-serif text-2xl mt-4">Full workbook (Excel)</h4>
          <p className="text-sm text-muted-foreground mt-1">
            One .xlsx with 4 sheets: Transactions, Donors, Categories, and Funds. Headers styled, currency formatted.
          </p>
          <Button
            className="mt-5 rounded-2xl soft-glow"
            onClick={() => download(
              `/organizations/${activeOrgId}/reports/workbook.xlsx${buildRangeQS()}`,
              `churchflow-workbook${fnameSuffix}.xlsx`,
            )}
            data-testid="export-workbook-button"
          >
            <FileDown className="w-4 h-4 mr-2" /> Download .xlsx
          </Button>
        </Card>

        <Card className="border-0 shadow-sm bg-card rounded-3xl p-6 transition-all hover:shadow-md">
          <FileSpreadsheet className="w-7 h-7 text-primary" />
          <h4 className="font-serif text-2xl mt-4">Transactions (CSV)</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Lightweight CSV for bookkeepers and spreadsheets.
          </p>
          <Button
            variant="outline"
            className="mt-5 rounded-2xl"
            onClick={() => download(
              `/organizations/${activeOrgId}/reports/transactions.csv${buildRangeQS()}`,
              `transactions${fnameSuffix}.csv`,
            )}
            data-testid="export-csv-button-data"
          >
            <FileDown className="w-4 h-4 mr-2" /> Download .csv
          </Button>
        </Card>
      </div>

      <Card className="border-0 shadow-sm bg-card/60 rounded-3xl p-6">
        <p className="text-sm text-foreground/80">
          Looking for polished PDFs (P&L, donor statements, headquarters report)? Head to the <strong>Reports</strong> tab.
        </p>
      </Card>

      {/* Data repair: default-fund misclassification (one-shot fix for the pre-Feb-2026 bug) */}
      <Card className="border-0 shadow-sm bg-card rounded-3xl p-6" data-testid="repair-default-fund-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Wrench className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <h4 className="font-serif text-2xl">Repair: Default-fund misclassification</h4>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Envelopes and checks left as &ldquo;default&rdquo; fund before the Feb 2026 fix were
              silently routed to whichever fund sorted first in the database —
              often Benevolence Fund. This tool finds those rows and moves them
              back to the intended default (General Fund). Preview first, apply when ready.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                variant="outline"
                className="rounded-2xl"
                disabled={repairBusy}
                onClick={async () => {
                  setRepairBusy(true);
                  try {
                    const { data } = await api.post(`/organizations/${activeOrgId}/repair/default-fund?apply=false`);
                    setRepair(data);
                    if (data.would_reclassify === 0) toast.success("Nothing to fix — you're all set.");
                    else toast(`${data.would_reclassify} transaction(s) would be moved to ${data.target_fund}. Review below.`);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setRepairBusy(false); }
                }}
                data-testid="repair-preview-button"
              >
                {repairBusy ? "Scanning…" : "Preview affected transactions"}
              </Button>
              {repair && repair.would_reclassify > 0 && !repair.applied && (
                <Button
                  className="rounded-2xl bg-amber-600 hover:bg-amber-700"
                  disabled={repairBusy}
                  onClick={async () => {
                    if (!window.confirm(`Reclassify ${repair.would_reclassify} transaction(s) to ${repair.target_fund}? This updates the ledger immediately.`)) return;
                    setRepairBusy(true);
                    try {
                      const { data } = await api.post(`/organizations/${activeOrgId}/repair/default-fund?apply=true`);
                      setRepair(data);
                      toast.success(`Reclassified ${data.applied_count} transaction(s) to ${data.target_fund}.`);
                    } catch (e) { toast.error(formatApiError(e)); }
                    finally { setRepairBusy(false); }
                  }}
                  data-testid="repair-apply-button"
                >
                  {repairBusy ? "Applying…" : `Reclassify ${repair.would_reclassify} row(s) to ${repair.target_fund}`}
                </Button>
              )}
            </div>

            {repair && (
              <div className="mt-5 rounded-2xl border border-border overflow-hidden" data-testid="repair-result">
                <div className={`px-4 py-3 text-xs uppercase tracking-wider ${repair.applied ? "bg-emerald-50 text-emerald-800" : "bg-secondary text-muted-foreground"} flex items-center gap-2`}>
                  {repair.applied ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {repair.applied
                    ? `Done — ${repair.applied_count} transaction(s) moved to ${repair.target_fund}.`
                    : `Preview — ${repair.would_reclassify} transaction(s) would be moved to ${repair.target_fund}.`}
                </div>
                {repair.sample && repair.sample.length > 0 && (
                  <table className="w-full text-xs">
                    <thead className="text-left uppercase tracking-wider text-muted-foreground text-[10px]">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Method</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2">Currently</th>
                        <th className="px-3 py-2">→ Will be</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repair.sample.map((s) => (
                        <tr key={s.id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 tabular-nums">{s.date}</td>
                          <td className="px-3 py-2">{s.payment_method}</td>
                          <td className="px-3 py-2 max-w-xs truncate">{s.description}</td>
                          <td className="px-3 py-2 text-destructive">{s.current_fund}</td>
                          <td className="px-3 py-2 text-primary">{s.new_fund}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatUSD(s.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {repair.would_reclassify > repair.sample.length && (
                  <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                    Showing first {repair.sample.length} of {repair.would_reclassify}. All will be moved when you apply.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
